import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import UnifiedMenu from '../components/UnifiedMenu';
import TextChat from '../components/TextChat';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageCircle, Mic, ChevronDown } from 'lucide-react';

const VOICE_OPTIONS = [
  { id: 'nova', name: 'Nova', desc: 'Warm & gentle', tone: 'female' },
  { id: 'shimmer', name: 'Shimmer', desc: 'Bright & soothing', tone: 'female' },
  { id: 'alloy', name: 'Alloy', desc: 'Calm & balanced', tone: 'neutral' },
  { id: 'echo', name: 'Echo', desc: 'Smooth & reassuring', tone: 'male' },
  { id: 'fable', name: 'Fable', desc: 'Soft & expressive', tone: 'neutral' },
];

export default function VoiceChatPage({ user, onLogout }) {
  const { language } = useLanguage();
  const [recentMood, setRecentMood] = useState(null);
  const [mode, setMode] = useState('text');
  const [selectedVoice, setSelectedVoice] = useState('nova');
  const [showVoiceMenu, setShowVoiceMenu] = useState(false);

  useEffect(() => {
    axios.get(`${API}/moods?limit=1`).then(r => {
      if (r.data?.length > 0) setRecentMood(r.data[0]);
    }).catch(() => {});
  }, []);

  const currentVoice = VOICE_OPTIONS.find(v => v.id === selectedVoice) || VOICE_OPTIONS[0];

  return (
    <div className="h-screen flex flex-col bg-gradient-to-b from-white via-stone-50 to-stone-100 overflow-hidden">
      <UnifiedMenu active="voice-chat" stats={null} currentQuote="" recentMoods={[]} onLogout={onLogout} />

      <main className="flex-1 overflow-hidden px-4 pt-2 pb-3 flex flex-col">
        <div className="zen-card p-3 flex-1 flex flex-col min-h-0">
          {/* Header — compact, not overlapping hamburger */}
          <div className="flex-none mb-2">
            <div className="flex items-center gap-2 pr-10">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-hunter-green to-sage flex items-center justify-center flex-shrink-0">
                {mode === 'voice' ? <Mic className="w-3.5 h-3.5 text-white" /> : <MessageCircle className="w-3.5 h-3.5 text-white" />}
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-semibold truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Chat with Mojo
                </h1>
              </div>

              {/* Mode Toggle — prominent */}
              <div className="flex bg-stone-200 rounded-xl p-1 ml-auto flex-shrink-0" data-testid="chat-mode-toggle">
                <button
                  onClick={() => setMode('text')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                    mode === 'text' ? 'bg-hunter-green text-white shadow-md' : 'text-warm-charcoal/60 hover:text-warm-charcoal'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  Text
                </button>
                <button
                  onClick={() => setMode('voice')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
                    mode === 'voice' ? 'bg-hunter-green text-white shadow-md' : 'text-warm-charcoal/60 hover:text-warm-charcoal'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  <Mic className="w-3.5 h-3.5" />
                  Voice
                </button>
              </div>
            </div>

            {/* Voice selector — only in voice mode */}
            {mode === 'voice' && (
              <div className="mt-2 relative">
                <button
                  onClick={() => setShowVoiceMenu(!showVoiceMenu)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-stone-50 border border-stone-200 text-xs w-full"
                  style={{ touchAction: 'manipulation' }}
                  data-testid="voice-selector"
                >
                  <span className="text-warm-charcoal/50">Voice:</span>
                  <span className="font-medium text-warm-charcoal">{currentVoice.name}</span>
                  <span className="text-warm-charcoal/40">— {currentVoice.desc}</span>
                  <ChevronDown className="w-3 h-3 text-warm-charcoal/40 ml-auto" />
                </button>

                {showVoiceMenu && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border border-stone-200 z-30 overflow-hidden">
                    {VOICE_OPTIONS.map(v => (
                      <button
                        key={v.id}
                        onClick={() => { setSelectedVoice(v.id); setShowVoiceMenu(false); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                          selectedVoice === v.id ? 'bg-hunter-green/5' : 'hover:bg-stone-50'
                        }`}
                        style={{ touchAction: 'manipulation' }}
                        data-testid={`voice-option-${v.id}`}
                      >
                        <div className={`w-2 h-2 rounded-full ${selectedVoice === v.id ? 'bg-hunter-green' : 'bg-stone-300'}`} />
                        <div>
                          <p className="text-xs font-medium text-warm-charcoal">{v.name}</p>
                          <p className="text-[10px] text-warm-charcoal/50">{v.desc} · {v.tone}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {recentMood && (
              <div className="mt-1.5 px-3 py-1 rounded-lg bg-sage/10 text-center">
                <p className="text-[10px] text-warm-charcoal/60">
                  Recent: <span className="font-medium text-hunter-green">{recentMood.specific_feeling || recentMood.primary_category}</span>
                </p>
              </div>
            )}
          </div>

          {/* Chat area */}
          <div className="flex-1 min-h-0">
            <TextChat user={user} language={language} voiceMode={mode === 'voice'} selectedVoice={selectedVoice} />
          </div>
        </div>
      </main>
    </div>
  );
}
